import React, { useState } from 'react';
import { X, Loader } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface NewMoveProcessModalProps {
    isBusy: boolean;
    onClose: () => void;
    onCreate: (name: string, description: string, scheduledFor?: string) => Promise<void>;
}

export const NewMoveProcessModal: React.FC<NewMoveProcessModalProps> = ({ isBusy, onClose, onCreate }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [scheduledFor, setScheduledFor] = useState('');

    const canCreate = !isBusy && name.trim().length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold">{t('manager.modules.moveProcesses.newModal.title', {}, 'Ny flytteplan')}</h2>
                    <button onClick={onClose} disabled={isBusy} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.newModal.nameLabel', {}, 'Navn *')}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            maxLength={100}
                            placeholder={t('manager.modules.moveProcesses.newModal.namePlaceholder', {}, "F.eks. 'Klargjøring for containermottak 15. april'")}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                            maxLength={500}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.newModal.scheduledLabel', {}, 'Planlagt utførelse')}</label>
                        <input
                            type="datetime-local"
                            value={scheduledFor}
                            onChange={e => setScheduledFor(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                    </div>
                </div>
                <div className="flex justify-end space-x-2 px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <button onClick={onClose} disabled={isBusy} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold">
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    <button
                        onClick={() => onCreate(name, description, scheduledFor || undefined)}
                        disabled={!canCreate}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center space-x-2"
                    >
                        {isBusy && <Loader className="w-4 h-4 animate-spin" />}
                        <span>{t('common.buttons.create', {}, 'Opprett')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
